import { combineReducers } from 'redux'
import { shallowEqual } from 'tangle-shared'
import * as isNumber from 'lodash/isNumber'
import * as uniq from 'lodash/uniq'
import {
  AUTHENTICATE_LINKAGE,
  CANCEL_MFA,
  CREATE_FAKE_PROVIDER_ACCOUNT,
  FLUSH_LIVE_UPDATE,
  GET_DETAILED_PROVIDER,
  GET_MIXED_ACCOUNTS,
  GET_PROVIDERS,
  GetProvidersPayload,
  PERFORM_MIXED_ACCOUNT_OPERATION,
  POLL_LIVE_UPDATE,
  POLL_PROVIDER_ACCOUNT,
  SET_MANUAL_BALANCE_ACCOUNT,
  SEARCH_PROVIDERS,
  CLEAR_PROVIDERS_SEARCH,
  CLEAR_RECENTLY_ADDED,
} from 'actions/linkageActions'
import { isFailAction, isRequestAction, isSuccessAction } from 'helpers/actionsHelpers'
import { Collection, Omit, Reducer } from 'interfaces'
import {
  AccountLinkStatus,
  LinkageDataState,
  LinkageForm,
  ManualBalanceAccount,
  MixedCompositeId,
  Provider,
  ProviderAccount,
  ProviderAccountState,
  ProviderBalanceAccount,
} from 'interfaces/linkage'
import { isManualBalanceAccount, isProviderAccount, isProviderBalanceAccount } from 'helpers/linkageHelpers'
import { needDiscardData, PROVIDER_ACCOUNTS_DISCARD_TRIGGERS, PROVIDER_BALANCE_ACCOUNTS_DISCARD_TRIGGERS } from 'helpers/discardTriggerHelpers'
import { concatCollections, getItemFromCollection, getItemsFromCollection, removeByKeyFromCollection, setItemInCollection } from 'helpers/normalizeHelpers'
import * as omit from 'lodash/omit'

export const providersReducer: Reducer<LinkageDataState['providers']> = (state = null, action) => {
  if ((action.type === GET_PROVIDERS || action.type === SEARCH_PROVIDERS) && action.meta === 'success') {
    return concatCollections<Provider, 'cid'>(state, action.payload.response)
  }

  if (action.type === GET_MIXED_ACCOUNTS && isSuccessAction(action)) {
    return concatCollections<Provider, 'cid'>(state, action.payload.response.providers)
  }

  if (action.type === AUTHENTICATE_LINKAGE && isSuccessAction(action)) {
    return setItemInCollection(state, action.payload.response.provider, 'cid', { checkEquality: true })
  }

  if (action.type === POLL_PROVIDER_ACCOUNT && isSuccessAction(action)) {
    return setItemInCollection(state, action.payload.response.provider, 'cid', { checkEquality: true })
  }

  return state
}

const providersSearchReducer: Reducer<LinkageDataState['searchQuery']> = (state = '', action) => {
  if (action.type === GET_PROVIDERS && isSuccessAction(action)) {
    const { params } = action.payload

    if (params.mode === 'search') {
      return params.query
    }
  }

  if (action.type === SEARCH_PROVIDERS && isSuccessAction(action)) {
    const { params } = action.payload

    return params.query
  }

  if (action.type === CLEAR_PROVIDERS_SEARCH) {
    return ''
  }

  return state
}

export const POPULAR_PROVIDER_SHOWN_COUNT = 6

const createProviderIdsReducer = (mode: GetProvidersPayload['mode']): Reducer<LinkageDataState['foundProviderCids' | 'popularProviderCids']> =>
  (state = null, action) => {
    if (action.type === GET_PROVIDERS && isSuccessAction(action) && action.payload.params.mode === mode) {
      const providerKeys = action.payload.response.allKeys
      return action.payload.params.mode === 'popular'
        ? providerKeys.slice(0, POPULAR_PROVIDER_SHOWN_COUNT)
        : providerKeys
    }

    if (action.type === SEARCH_PROVIDERS && isSuccessAction(action) && mode === 'search') {
      const providerKeys = action.payload.response.allKeys
      return providerKeys
    }

    if (action.type === CLEAR_PROVIDERS_SEARCH && mode === 'search') {
      return null
    }

    return state
  }

export const foundProviderCidsReducer = createProviderIdsReducer('search')
export const popularProviderCidsReducer = createProviderIdsReducer('popular')

const setProviderAccountInCollection = (state: LinkageDataState['providerAccounts'], providerAccount: ProviderAccount): LinkageDataState['providerAccounts'] => {
  const { replaced_id } = providerAccount.meta

  const nextState = setItemInCollection(state, providerAccount, 'cid', { checkEquality: true })

  if (!isNumber(replaced_id)) {
    return nextState
  }

  const replacedProviderAccount = getItemsFromCollection(nextState).find(({ id }) => id === replaced_id)
  if (!replacedProviderAccount) {
    return nextState
  }

  return removeByKeyFromCollection(nextState, replacedProviderAccount.cid)
}

export const providerAccountsReducer: Reducer<LinkageDataState['providerAccounts']> = (state = null, action) => {
  if (needDiscardData(PROVIDER_ACCOUNTS_DISCARD_TRIGGERS)(action)) {
    return null
  }

  if (action.type === GET_MIXED_ACCOUNTS && isSuccessAction(action)) {
    return concatCollections<ProviderAccount, 'cid'>(state, action.payload.response.providerAccounts)
  }

  if (action.type === AUTHENTICATE_LINKAGE && isSuccessAction(action)) {
    return setProviderAccountInCollection(state, action.payload.response.providerAccount)
  }

  if (action.type === POLL_PROVIDER_ACCOUNT && isSuccessAction(action)) {
    return setProviderAccountInCollection(state, action.payload.response.providerAccount)
  }

  if (action.type === CREATE_FAKE_PROVIDER_ACCOUNT) {
    return setItemInCollection(state, action.payload, 'cid')
  }

  if (action.type === PERFORM_MIXED_ACCOUNT_OPERATION && action.payload.params.operation === 'delete') {
    const { account } = action.payload.params
    if (isRequestAction(action) && isProviderAccount(account)) {
      return removeByKeyFromCollection<ProviderAccount, 'cid'>(state, account.cid)
    }
    if (isFailAction(action) && isProviderAccount(account)) {
      return setItemInCollection(state, account, 'cid')
    }

    if (isProviderBalanceAccount(account)) {
      const providerBalanceAccountCid = account.cid
      const providerAccount = getItemFromCollection<ProviderAccount, 'cid'>(state, account.providerAccountCid)
      if (!providerAccount) {
        return state
      }

      if (isRequestAction(action)) {
        const nextProviderAccount = {
          ...providerAccount,
          providerBalanceAccountCids: providerAccount.providerBalanceAccountCids
            .filter((cid) => cid !== providerBalanceAccountCid),
        }

        return setItemInCollection(state, nextProviderAccount, 'cid')
      }

      if (isFailAction(action)) {
        const nextProviderAccount = {
          ...providerAccount,
          providerBalanceAccountCids: uniq(providerAccount.providerBalanceAccountCids.concat(providerBalanceAccountCid)),
        }

        return setItemInCollection(state, nextProviderAccount, 'cid')
      }
    }
  }

  if (action.type === CANCEL_MFA && isSuccessAction(action)) {
    return setItemInCollection(state, action.payload.response.providerAccount, 'cid')
  }

  return state
}

const PREDEFINED_FAILED_STATE: Omit<ProviderAccountState, 'cid'> = {
  status: 'failed',
  message: 'Authentication failed: connection error',
  notification_type: 'failure',
  extra_message: 'Sorry, we are temporarily unable to connect to institution. Please try again later or [link your account manually](action:suggest_manual_account).',
  actions: [
    { id: 'retry', title: 'Retry' },
    { id: 'delete', title: 'Delete' },
    { id: 'suggest_manual_account' },
  ],
}

const setPredefinedFailedState = (state: LinkageDataState['providerAccountStates'], cid: MixedCompositeId): LinkageDataState['providerAccountStates'] =>
  setItemInCollection(state, { ...PREDEFINED_FAILED_STATE, cid }, 'cid')

const providerAccountStatesReducer: Reducer<LinkageDataState['providerAccountStates']> = (state = null, action) => {
  if (needDiscardData(PROVIDER_ACCOUNTS_DISCARD_TRIGGERS)(action)) {
    return null
  }

  if (action.type === GET_MIXED_ACCOUNTS && isSuccessAction(action)) {
    return concatCollections<ProviderAccountState, 'cid'>(state, action.payload.response.providerAccountStates)
  }

  if (action.type === GET_DETAILED_PROVIDER && isFailAction(action)) {
    return setPredefinedFailedState(state, action.payload.params.session.cid)
  }

  if (action.type === AUTHENTICATE_LINKAGE && isSuccessAction(action)) {
    return setItemInCollection(state, action.payload.response.providerAccountState, 'cid')
  }

  if (action.type === POLL_PROVIDER_ACCOUNT && isSuccessAction(action)) {
    return setItemInCollection(state, action.payload.response.providerAccountState, 'cid')
  }

  if (action.type === CREATE_FAKE_PROVIDER_ACCOUNT) {
    return setPredefinedFailedState(state, action.payload.cid)
  }

  if (
    action.type === PERFORM_MIXED_ACCOUNT_OPERATION
    && action.payload.params.operation === 'delete'
    && isSuccessAction(action)
    && isProviderAccount(action.payload.params.account)
  ) {
    return removeByKeyFromCollection<ProviderAccountState, 'cid'>(state, action.payload.params.account.cid)
  }

  if (action.type === CANCEL_MFA && isSuccessAction(action)) {
    return setItemInCollection(state, action.payload.response.providerAccountState, 'cid')
  }

  return state
}

export const providerBalanceAccountsReducer: Reducer<LinkageDataState['providerBalanceAccounts']> = (state = null, action) => {
  if (needDiscardData(PROVIDER_BALANCE_ACCOUNTS_DISCARD_TRIGGERS)(action)) {
    return null
  }

  if (action.type === GET_MIXED_ACCOUNTS && isSuccessAction(action)) {
    return concatCollections<ProviderBalanceAccount, 'cid'>(state, action.payload.response.providerBalanceAccounts)
  }

  if (action.type === AUTHENTICATE_LINKAGE && isSuccessAction(action)) {
    return concatCollections<ProviderBalanceAccount, 'cid'>(state, action.payload.response.providerBalanceAccounts)
  }

  if (action.type === POLL_PROVIDER_ACCOUNT && isSuccessAction(action)) {
    return concatCollections<ProviderBalanceAccount, 'cid'>(state, action.payload.response.providerBalanceAccounts)
  }

  if (action.type === PERFORM_MIXED_ACCOUNT_OPERATION) {
    const { account, operation } = action.payload.params
    switch (operation) {
      case 'delete': {
        // duplicating `isProviderBalanceAccount(action.payload.params)` is a sacrifice to a TS god
        if (isRequestAction(action) && isProviderBalanceAccount(account)) {
          return removeByKeyFromCollection<ProviderBalanceAccount, 'cid'>(state, account.cid)
        }
        /*
          we can't restore provider balance accounts upon failed deletion
          because `action.payload.params.providerBalanceAccountCids` contains only cids.
          Therefore we actually remove them only upon succeeded deletion (`isSuccessAction`)
         */
        if (isSuccessAction(action) && isProviderAccount(account)) {
          return removeByKeyFromCollection<ProviderBalanceAccount, 'cid'>(state, ...(account.providerBalanceAccountCids || []))
        }
      }

      case 'link':
      case 'unlink': {
        if (isRequestAction(action) && isProviderBalanceAccount(account)) {
          const link_status: AccountLinkStatus = operation === 'link' ? 'active' : 'unlinked'
          return setItemInCollection(state as Collection<any, 'cid'>, { ...account, link_status }, 'cid')
        }
      }
    }

    if (isFailAction(action) && isProviderBalanceAccount(account)) {
      return setItemInCollection(state, account, 'cid')
    }
  }

  return state
}

const manualBalanceAccountsReducer: Reducer<LinkageDataState['manualBalanceAccounts']> = (state = null, action) => {
  if (needDiscardData(PROVIDER_ACCOUNTS_DISCARD_TRIGGERS)(action)) {
    return null
  }

  if (action.type === GET_MIXED_ACCOUNTS && isSuccessAction(action)) {
    return concatCollections<ManualBalanceAccount, 'cid'>(state, action.payload.response.manualBalanceAccounts)
  }

  if (action.type === PERFORM_MIXED_ACCOUNT_OPERATION) {
    const { operation, account } = action.payload.params
    switch (operation) {
      case 'delete': {
        // duplicating `isManualBalanceAccount(action.payload.params)` is a sacrifice to a TS god
        if (isRequestAction(action) && isManualBalanceAccount(account)) {
          return removeByKeyFromCollection<ManualBalanceAccount, 'cid'>(state, account.cid)
        }
      }

      case 'link':
      case 'unlink': {
        if (isRequestAction(action) && isManualBalanceAccount(account)) {
          const link_status: AccountLinkStatus = operation === 'link' ? 'active' : 'unlinked'
          return setItemInCollection<ManualBalanceAccount, 'cid'>(state, { ...account, link_status }, 'cid')
        }
      }
    }

    if (isFailAction(action) && isManualBalanceAccount(account)) {
      return setItemInCollection(state, account, 'cid')
    }
  }

  if (action.type === SET_MANUAL_BALANCE_ACCOUNT) {
    return setItemInCollection(state, action.payload, 'cid')
  }

  return state
}

const linkageFormsReducer: Reducer<LinkageDataState['linkageForms']> = (state = null, action) => {
  if (action.type === GET_DETAILED_PROVIDER && isSuccessAction(action)) {
    const {
      cid = action.payload.params.session.cid,
      ...restForm
    } = action.payload.response.form

    return setItemInCollection(state, {
      cid,
      ...restForm,
    }, 'cid')
  }

  if (action.type === GET_MIXED_ACCOUNTS && isSuccessAction(action)) {
    return concatCollections<LinkageForm, 'cid'>(state, action.payload.response.forms)
  }

  if (action.type === POLL_PROVIDER_ACCOUNT && isSuccessAction(action)) {
    const { form } = action.payload.response
    if (form) {
      return setItemInCollection(state, form, 'cid')
    }

    return state
  }

  if (action.type === CANCEL_MFA && isSuccessAction(action)) {
    const { cid } = action.payload.params

    return removeByKeyFromCollection<LinkageForm, 'cid'>(state, cid)
  }

  return state
}

const liveUpdateReducer: Reducer<LinkageDataState['liveUpdate']> = (state = null, action) => {
  if (action.type === POLL_LIVE_UPDATE && isSuccessAction(action)) {
    return shallowEqual(state, action.payload.response) ? state : action.payload.response
  }


  if (action.type === FLUSH_LIVE_UPDATE && isSuccessAction(action)) {
    return shallowEqual(state, action.payload.response) ? state : action.payload.response
  }

  return state
}

const INITIAL_STATE = {}
const recentlyAddedAccounts: Reducer<LinkageDataState['recentlyAddedAccounts']> = (prevState = INITIAL_STATE, action) => {
  switch (action.type) {
    case AUTHENTICATE_LINKAGE: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const { providerAccount } = action.payload.response
      return {
        ...prevState,
        [providerAccount.cid]: true,
      }
    }

    case CLEAR_RECENTLY_ADDED: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const { providerAccountCids } = action.payload.response
      return providerAccountCids.reduce(omit, prevState)
    }
  }

  return prevState
}

export default combineReducers<LinkageDataState>({
  providers: providersReducer,
  searchQuery: providersSearchReducer,
  foundProviderCids: foundProviderCidsReducer,
  popularProviderCids: popularProviderCidsReducer,

  providerAccounts: providerAccountsReducer,
  providerAccountStates: providerAccountStatesReducer,
  providerBalanceAccounts: providerBalanceAccountsReducer,
  manualBalanceAccounts: manualBalanceAccountsReducer,
  linkageForms: linkageFormsReducer,

  recentlyAddedAccounts,

  liveUpdate: liveUpdateReducer,
})
